// 품질 검사 (3D 측정기 자동 수집)
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { SkeletonRows } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

interface Insp {
  inspection_id: string
  work_order_id: string
  measurement_value: number
  pass_fail: 'Pass' | 'Fail'
  collected_at: string
}

export function InspectionPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'inspection_data', 'write')
  const [list, setList] = useState<Insp[] | null>(null)
  const [form, setForm] = useState({ work_order_id: 'WO-2026-0001', measurement_value: 0.002, pass_fail: 'Pass' as 'Pass' | 'Fail' })
  const [busy, setBusy] = useState(false)

  const reload = async () => {
    try { setList(await api.get<Insp[]>('/inspection')) }
    catch (e: any) { toast.push('error', e?.message ?? t('toast.fetchFailed')) }
  }
  useEffect(() => { reload() }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await api.post('/inspection', form)
      toast.push('success', t('insp.saved', { judgment: form.pass_fail }))
      reload()
    } catch (er: any) {
      toast.push('error', er?.message ?? t('toast.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <h1 className="page-title reveal">
        <FontAwesomeIcon icon="magnifying-glass-chart" /> {t('insp.title')}
      </h1>
      <p className="muted reveal">{t('insp.intro')}</p>

      {canWrite && (
        <Card glass tilt={false} spotlight={false} as="section" aria-labelledby="ins-h" className="reveal">
          <h2 id="ins-h" style={{ fontSize: 'var(--fs-lg)' }}>{t('insp.manual')}</h2>
          <form onSubmit={submit} className="row" style={{ alignItems: 'end', gap: '0.75rem' }}>
            <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label htmlFor="wo">{t('insp.field.wo')}</label>
              <input
                id="wo"
                placeholder="WO-2026-0001"
                value={form.work_order_id}
                onChange={(e) => setForm({ ...form, work_order_id: e.target.value })}
                required
              />
            </div>
            <div className="field" style={{ flex: '1 1 200px', marginBottom: 0 }}>
              <label htmlFor="mv">{t('insp.field.measurement')}</label>
              <input
                id="mv" type="number" step="0.0001" min="0"
                placeholder="0.0020"
                value={form.measurement_value}
                onChange={(e) => setForm({ ...form, measurement_value: Number(e.target.value) })}
                required
              />
            </div>
            <div className="field" style={{ flex: '0 1 160px', marginBottom: 0 }}>
              <label htmlFor="pf">{t('insp.field.judgment')}</label>
              <select id="pf" value={form.pass_fail} onChange={(e) => setForm({ ...form, pass_fail: e.target.value as 'Pass' | 'Fail' })}>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
              </select>
            </div>
            <Button type="submit" variant="accent" disabled={busy} aria-busy={busy}>
              <FontAwesomeIcon icon={busy ? 'circle-notch' : 'check'} spin={busy} />
              <span>{t('common.save')}</span>
            </Button>
          </form>
        </Card>
      )}

      <Card glass tilt={false} spotlight={false} className="reveal">
        {!list ? (
          <SkeletonRows rows={6} />
        ) : list.length === 0 ? (
          <div className="empty-state" role="status">
            <FontAwesomeIcon icon="magnifying-glass-chart" className="icon-lg" />
            <p className="muted" style={{ margin: 0 }}>{t('insp.empty')}</p>
            <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-sm)' }}>
              {t('insp.emptyHint')}
            </p>
          </div>
        ) : (
          <div className="table-wrap" style={{ '--table-min-w': '720px' } as React.CSSProperties}>
            <table className="table" aria-label={t('insp.aria.table')}>
              <thead>
                <tr><th>{t('insp.col.id')}</th><th>{t('insp.col.wo')}</th><th>{t('insp.col.measurement')}</th><th>{t('insp.col.judgment')}</th><th>{t('insp.col.collectedAt')}</th></tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.inspection_id}>
                    <td className="mono">{r.inspection_id}</td>
                    <td className="mono">{r.work_order_id}</td>
                    <td className="mono">{r.measurement_value}</td>
                    <td>
                      <span className={`badge ${r.pass_fail === 'Pass' ? 'success' : 'danger'}`}>
                        <FontAwesomeIcon icon={r.pass_fail === 'Pass' ? 'circle-check' : 'circle-exclamation'} />
                        {r.pass_fail}
                      </span>
                    </td>
                    <td>{new Date(r.collected_at).toLocaleString('ko-KR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </main>
  )
}
